import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sparkles, RefreshCw, Loader2, ChevronDown, ChevronUp, LogIn, LogOut,
  BedDouble, Clipboard, ExternalLink, Save, Link2, Baby, Bed,
} from "lucide-react";

/**
 * CleaningPlanTab (iter217 — 02/2026)
 * Menu "Pulizia da assegnare" dentro Adempimenti e Report.
 * Programma la pulizia della giornata (default: DOMANI) con 3 sezioni
 * collassabili + link condivisibile per collaboratori.
 */

const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const fmtDayLong = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const CleaningCheckbox = ({ label, checked, onChange, icon: Icon }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#AC5D45]" />
    {Icon && <Icon className="w-4 h-4 text-stone-500" />}
    <span>{label}</span>
  </label>
);

const NumberField = ({ label, value, onChange }) => (
  <label className="inline-flex items-center gap-1.5 text-sm">
    <span className="text-stone-600">{label}</span>
    <input
      type="number"
      min={0}
      max={9}
      value={value ?? 0}
      onChange={(e) => onChange(Math.max(0, Math.min(9, parseInt(e.target.value || "0", 10))))}
      className="w-14 h-7 text-center border rounded text-sm"
    />
  </label>
);

const Section = ({ title, count, color, defaultOpen, icon: Icon, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-xl border ${color.border} bg-white overflow-hidden`} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full px-4 py-3 flex items-center justify-between ${color.bg} hover:brightness-95 transition`}
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 ${color.icon}`} />}
          <span className={`font-display text-base ${color.text}`}>{title}</span>
          {count > 0 && (
            <span className={`ml-1 text-white text-xs font-bold rounded-full px-2 py-0.5 ${color.badge}`}>
              {count}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {open && <div className="p-3 space-y-2">{children}</div>}
    </div>
  );
};

export default function CleaningPlanTab() {
  const [date, setDate] = useState(tomorrowISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [saved, setSaved] = useState(false);
  const [state, setState] = useState({}); // per-room state (bed + cleaning + notes)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get(`/admin/cleaning-plan?date=${date}`);
      setData(r);
      // Merge saved rooms into local state
      const savedRooms = r?.saved_state?.rooms || {};
      const initState = {};
      // For checkouts: bed suggestions
      (r?.checkouts || []).forEach(c => {
        const key = c.code;
        initState[key] = savedRooms[key] || {
          beds: { ...(c.bed_suggestion || {}) },
          notes: "",
          extra_requests_ack: false,
        };
      });
      // For staying: cleaning checkboxes
      (r?.staying || []).forEach(s => {
        const key = s.code;
        initState[key] = savedRooms[key] || {
          cleaning: { asciugamani: false, lenzuola: false, veloce: false, profonda: false },
          notes: "",
        };
      });
      setState(initState);
      setSaved(!!r?.saved_state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento piano pulizie");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const updateRoom = (code, patch) => {
    setState(prev => ({ ...prev, [code]: { ...(prev[code] || {}), ...patch } }));
    setSaved(false);
  };

  const savePlan = async () => {
    try {
      await api.post("/admin/cleaning-plan/save", { date, rooms: state });
      setSaved(true);
      toast.success("Piano salvato ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const generateShareLink = async () => {
    setSavingLink(true);
    try {
      // Save first
      await api.post("/admin/cleaning-plan/save", { date, rooms: state });
      const { data: r } = await api.post("/admin/cleaning-plan/share-link", { date });
      const url = `${window.location.origin}/pulizie/${r.token}`;
      await navigator.clipboard.writeText(`Ciao, ecco i lavori di ${fmtDayLong(date)}: ${url}`);
      toast.success("Link copiato negli appunti · incollalo su WhatsApp", { duration: 10000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione link");
    } finally {
      setSavingLink(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="cleaning-plan-tab">
      {/* Header */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#AC5D45]" />
            <h2 className="font-display text-xl text-[#1E3A5F]">Pulizia da assegnare</h2>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="text-sm px-3 py-1.5 border rounded-lg"
              data-testid="cleaning-plan-date"
            />
            <Button size="sm" variant="outline" onClick={load} data-testid="cleaning-plan-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#635F59] mt-2">
          Programma le pulizie di <b>{fmtDayLong(date)}</b>. Compila checklist e configurazione letti, poi genera il link da inviare via WhatsApp alle collaboratrici.
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#AC5D45]" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* 🟢 ARRIVI */}
          <Section
            title="Nuovi arrivi"
            count={data.arrivals?.length || 0}
            color={{ bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", icon: "text-emerald-600", badge: "bg-emerald-600" }}
            defaultOpen={true}
            icon={LogIn}
            testId="cleaning-arrivals"
          >
            {(data.arrivals || []).length === 0 && (
              <div className="text-sm text-stone-500 italic">Nessun arrivo previsto</div>
            )}
            {(data.arrivals || []).map(a => (
              <div key={a.code} className="border border-emerald-100 rounded-lg p-3 bg-emerald-50/40" data-testid={`arrival-${a.code}`}>
                <div className="flex flex-wrap gap-x-2 items-baseline text-sm">
                  <span className="font-semibold text-[#1E3A5F]">📍 {a.room_name || "-"}</span>
                  {a.room_slot && <span className="text-stone-500">· vaschetta {a.room_slot}</span>}
                  <span className="text-stone-500">·</span>
                  <span className="font-semibold">{a.guest_name}</span>
                  <span className="text-stone-500">·</span>
                  <span>{a.counts.adults} adulti{a.counts.children > 0 && ` + ${a.counts.children} bambini`}</span>
                  {a.counts.children_ages?.length > 0 && (
                    <span className="text-stone-500">({a.counts.children_ages.join(", ")} anni)</span>
                  )}
                </div>
                {a.arrival_time && <div className="text-xs text-stone-600 mt-1">arrivo previsto ore {a.arrival_time}</div>}
                {a.special_requests?.length > 0 && (
                  <div className="mt-1 text-xs bg-white border border-emerald-200 rounded p-2">
                    <b>Richieste ospite:</b> {a.special_requests.join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </Section>

          {/* 🔴 CHECK-OUT */}
          <Section
            title="Check-out → CAMERE DA RIFARE"
            count={data.checkouts?.length || 0}
            color={{ bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "text-red-600", badge: "bg-red-600" }}
            defaultOpen={true}
            icon={LogOut}
            testId="cleaning-checkouts"
          >
            {(data.checkouts || []).length === 0 && (
              <div className="text-sm text-stone-500 italic">Nessun check-out previsto</div>
            )}
            {(data.checkouts || []).map(c => {
              const st = state[c.code] || {};
              const beds = st.beds || {};
              return (
                <div key={c.code} className="border border-red-100 rounded-lg p-3 bg-red-50/30" data-testid={`checkout-${c.code}`}>
                  <div className="flex flex-wrap gap-x-2 items-baseline text-sm">
                    <span className="font-semibold text-[#1E3A5F]">🛏️ {c.room_name || "-"}</span>
                    {c.room_slot && <span className="text-stone-500">· vaschetta {c.room_slot}</span>}
                  </div>
                  <div className="text-xs text-stone-600 mt-0.5">Esce: <b>{c.guest_name}</b></div>
                  {c.next_guest ? (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                      <div className="font-bold text-amber-800">⚠️ Prossimo ospite (arriva {c.next_guest.check_in}):</div>
                      <div>{c.next_guest.guest_name} · {c.next_guest.counts.adults} adulti
                        {c.next_guest.counts.children > 0 && ` + ${c.next_guest.counts.children} bambini`}
                        {c.next_guest.counts.children_ages?.length > 0 && ` (${c.next_guest.counts.children_ages.join(", ")} anni)`}
                      </div>
                      {c.next_guest.special_requests?.length > 0 && (
                        <div className="mt-1"><b>Richieste:</b> {c.next_guest.special_requests.join(" · ")}</div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-stone-500 italic">Camera resta libera domani (nessun nuovo ospite)</div>
                  )}
                  {/* Bed config */}
                  <div className="mt-3">
                    <div className="text-xs font-bold text-stone-700 mb-1">Configurazione letti da preparare:</div>
                    <div className="flex flex-wrap gap-3 items-center">
                      <NumberField label="Matrimoniali" value={beds.matrimoniali} onChange={(v) => updateRoom(c.code, { beds: { ...beds, matrimoniali: v } })} />
                      <NumberField label="Singoli" value={beds.singoli} onChange={(v) => updateRoom(c.code, { beds: { ...beds, singoli: v } })} />
                      <NumberField label="Letti aggiunti" value={beds.letti_aggiunti} onChange={(v) => updateRoom(c.code, { beds: { ...beds, letti_aggiunti: v } })} />
                      <CleaningCheckbox label="Culla" checked={beds.culla} onChange={(v) => updateRoom(c.code, { beds: { ...beds, culla: v } })} icon={Baby} />
                    </div>
                  </div>
                  {/* Notes */}
                  <textarea
                    placeholder="Note libere per la collaboratrice..."
                    value={st.notes || ""}
                    onChange={(e) => updateRoom(c.code, { notes: e.target.value })}
                    className="mt-2 w-full text-sm border rounded p-2 min-h-[50px]"
                    data-testid={`checkout-notes-${c.code}`}
                  />
                </div>
              );
            })}
          </Section>

          {/* 🟡 STAYING */}
          <Section
            title="Camere occupate → RIFINITURA"
            count={data.staying?.length || 0}
            color={{ bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: "text-amber-600", badge: "bg-amber-600" }}
            defaultOpen={false}
            icon={BedDouble}
            testId="cleaning-staying"
          >
            {(data.staying || []).length === 0 && (
              <div className="text-sm text-stone-500 italic">Nessun ospite in soggiorno intermedio</div>
            )}
            {(data.staying || []).map(s => {
              const st = state[s.code] || {};
              const cl = st.cleaning || {};
              return (
                <div key={s.code} className="border border-amber-100 rounded-lg p-3 bg-amber-50/40" data-testid={`staying-${s.code}`}>
                  <div className="flex flex-wrap gap-x-2 items-baseline text-sm">
                    <span className="font-semibold text-[#1E3A5F]">🛏️ {s.room_name || "-"}</span>
                    <span className="text-stone-500">—</span>
                    <span className="font-semibold">{s.guest_name}</span>
                    {s.nights?.night_num && (
                      <span className="text-stone-500">(notte {s.nights.night_num} di {s.nights.total_nights})</span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    <CleaningCheckbox label="Cambio asciugamani" checked={cl.asciugamani} onChange={(v) => updateRoom(s.code, { cleaning: { ...cl, asciugamani: v } })} />
                    <CleaningCheckbox label="Cambio lenzuola" checked={cl.lenzuola} onChange={(v) => updateRoom(s.code, { cleaning: { ...cl, lenzuola: v } })} />
                    <CleaningCheckbox label="Pulizia veloce" checked={cl.veloce} onChange={(v) => updateRoom(s.code, { cleaning: { ...cl, veloce: v } })} />
                    <CleaningCheckbox label="Pulizia profonda" checked={cl.profonda} onChange={(v) => updateRoom(s.code, { cleaning: { ...cl, profonda: v } })} />
                  </div>
                  {s.special_requests?.length > 0 && (
                    <div className="mt-2 text-xs bg-white border border-amber-200 rounded p-2">
                      <b>Richieste ospite:</b> {s.special_requests.join(" · ")}
                    </div>
                  )}
                  <textarea
                    placeholder="Note libere..."
                    value={st.notes || ""}
                    onChange={(e) => updateRoom(s.code, { notes: e.target.value })}
                    className="mt-2 w-full text-sm border rounded p-2 min-h-[40px]"
                    data-testid={`staying-notes-${s.code}`}
                  />
                </div>
              );
            })}
          </Section>

          {/* Bottom bar */}
          <div className="bg-white border border-[#E8E2D9] rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 sticky bottom-0">
            <div className="text-xs text-[#635F59]">
              {saved ? "✓ Piano salvato" : "Modifiche non salvate"}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={savePlan} data-testid="cleaning-plan-save">
                <Save className="w-4 h-4 mr-1" /> Salva
              </Button>
              <Button
                size="sm"
                onClick={generateShareLink}
                disabled={savingLink}
                className="bg-[#25D366] hover:bg-[#128C7E] text-white"
                data-testid="cleaning-plan-share"
              >
                {savingLink ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
                Copia link WhatsApp
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
